import IconButton from "@mui/material/IconButton";
import { alpha, styled } from "@mui/material/styles";

/**
 * A delete button that is neutral until it is pointed at, then red.
 *
 * This is the shape a destructive action takes when it sits on its own — a trash icon beside a list
 * row, a chip, an attachment, a fee category. It is not the shape it takes in a table's actions
 * column, which is red at rest; see
 * [table row actions](../../../../../docs/software-design/table-row-actions.md) for why those two
 * answers differ and `_shared/_views/tables/row-actions.tsx` for that one.
 *
 * The difference is how much company the button keeps. An actions column is a column: every cell in
 * it is a button, the eye already knows where to look, and red is what separates the one that
 * destroys from the three that do not. An inline trash has no such column. It sits next to content,
 * often one of several rows of it, and a standing red icon per row turns a list into a hazard sign —
 * so it waits, neutral, and commits to red under the cursor and under keyboard focus alike.
 *
 * Colour is not the only channel either way. Every call site puts this button in a `Tooltip` and
 * gives it an `aria-label`, so nothing here is load-bearing for a reader who cannot separate the two
 * colours.
 *
 * Pass the icon as the child — `DeleteIcon`, `DeleteOutlinedIcon`, a `CircularProgress` while the
 * deletion is in flight — and pass no `color` on it. The glyph inherits from the button, which is
 * what lets one rule move both the icon and its ripple. Sizing is the call site's too: `size`,
 * `edge` and `fontSize` all behave as they do on a plain `IconButton`, and `disabled` still greys
 * out through MUI's own `.Mui-disabled`, which outranks the resting colour set here.
 */
const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: alpha(theme.palette.error.main, theme.palette.action.hoverOpacity),
    color: theme.palette.error.main,
  },
}));

export default DeleteIconButton;
